import React, { useEffect, useState } from 'react';
import { Button, Input, Modal } from 'antd';
import { separate } from '../../model/separate';
import { api_product, api_product_lookup_code } from '../../api/product';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CommonSearch from '../common/common-search';
import { api_config_separate } from '../../api/config';
import { api_separate } from '../../api/separate';
import { isEmpty } from '../../utils/util';

interface props {
    separateData: separate,
    onChange: React.Dispatch<React.SetStateAction<object>>
}

const SeparateDetail: React.FC<props> = (prop) => {

    const [isOpen, setIsOpen] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [tableSelect, setTableSelect] = useState('');
    const [flag, setFlag] = useState('');

    const [data, setData] = useState({});
    const [productList, setProductList] = useState([]);

    const [fromProductCode, setFromProductCode] = useState('');
    const [fromProductName, setFromProductName] = useState('');
    const [fromQuantity, setFromQuantity] = useState(0);
    const [fromProductUnitName, setFromProductUnitName] = useState('');
    const [fromProductCost, setFromProductCost] = useState(0);

    const [toProductCode, setToProductCode] = useState('');
    const [toProductName, setToProductName] = useState('');
    const [toProductPrice, setToProductPrice] = useState(0);
    const [toQuantity, setToQuantity] = useState(0);
    const [configFromQuantity, setConfigFromQuantity] = useState(1);
    const [configToQuantity, setConfigToQuantity] = useState(0);
    const [toProductUnitName, setToProductUnitName] = useState('');
    const [toProductCost, setToProductCost] = useState(0);
    const [remark, setRemark] = useState('');

    const init = async () => {
        setData(prop.separateData);
        setInputData(prop.separateData);

        let getProduct = await api_product('LIST', {});
        setProductList(Array.isArray(getProduct) ? getProduct : (getProduct?.items || []));

        if (prop.separateData.id > 0) {
            // The row handed in here (from the /separate list) only carries
            // code/name/quantity -- not cost/price. Fetch the single
            // document's own authoritative, immutable snapshot (source_unit_
            // cost, destination_unit_cost, config_from_quantity, sale_price_
            // after, ...) from the hardened Backend instead of cross-
            // referencing the separately-loaded (paginated, so possibly
            // missing this exact Product) productList -- that cross-
            // reference could throw when the Product wasn't on the loaded
            // page, aborting the rest of this block and leaving cost/price
            // blank, and even when it didn't throw it was reading today's
            // live Product cost, not what was actually charged when this
            // document was confirmed.
            try {
                let detail: any = await api_separate('GET', {}, prop.separateData.id);
                if (detail && detail.id > 0) {
                    let object: any = { ...prop.separateData, ...detail };
                    object['from_product_cost'] = detail['source_unit_cost'];
                    object['to_product_price'] = detail['sale_price_after'] != null ? detail['sale_price_after'] : detail['sale_price_before'];
                    object['config_from_quantity'] = detail['config_from_quantity'];
                    object['config_to_quantity'] = detail['config_to_quantity'];
                    setData(object);
                    setInputData(object);
                    // setInputData() above just recomputed toProductCost via
                    // calculateToProductCost(), which derives cost from
                    // from_product_cost/config_from_quantity/config_to_quantity
                    // -- only valid as a live ADD-mode preview formula. It
                    // silently diverges from what this document actually cost
                    // whenever the real from_quantity/to_quantity used in this
                    // transaction isn't the same ratio as the base config
                    // (e.g. 2 packs -> 12 units here, vs. the 1-pack base
                    // ratio baked into config_from_quantity/config_to_quantity,
                    // recomputing 216*1/12=18 instead of the actual 432/12=36).
                    // The Backend already persisted the authoritative snapshot
                    // as destination_unit_cost at confirmation time -- use it
                    // directly instead of re-deriving history.
                    if (!isEmpty(detail['destination_unit_cost'])) {
                        setToProductCost(detail['destination_unit_cost']);
                    }
                }
            } catch (err) {
                console.error('Failed to load separate document detail:', err);
            }
        }
    }

    const initSeparate = () => {
        let object: any = {};
        object['from_product'] = 0;
        object['from_product_code'] = '';
        object['from_product_name'] = '';
        object['from_product_unit_name'] = '';
        object['from_product_cost'] = 0;
        object['from_quantity'] = 0;
        
        object['to_product'] = 0;
        object['to_product_code'] = '';
        object['to_product_name'] = '';
        object['to_product_unit_name'] = '';
        object['to_product_cost'] = 0;
        object['to_product_price'] = 0;
        object['config_from_quantity'] = 0;
        object['config_to_quantity'] = 0;
        object['to_quantity'] = 0;

        return object;
    } 

    const getConfigModal = (titile: string, content: string) => {
        return {
            title: titile,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: content,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
            centered: true
        }
    }

    const setInputData = (object: any) => {
        setFromProductCode(object.from_product_code != null ? object.from_product_code : '');
        setFromProductName(object.from_product_name != null ? object.from_product_name : '');
        setFromQuantity(object.from_quantity != null ? object.from_quantity : '');
        setFromProductUnitName(object.from_product_unit_name != null ? object.from_product_unit_name : '');
        setFromProductCost(object.from_product_cost != null ? object.from_product_cost : '');

        setToProductCode(object.to_product_code != null ? object.to_product_code : '');
        setToProductName(object.to_product_name != null ? object.to_product_name : '');
        setToProductPrice(object.to_product_price != null ? object.to_product_price : '');
        setToQuantity(object.to_quantity != null ? object.to_quantity : '');
        setConfigFromQuantity(object.config_from_quantity != null && object.config_from_quantity > 0 ? object.config_from_quantity : 1);
        setConfigToQuantity(object.config_to_quantity != null ? object.config_to_quantity : '');
        setToProductUnitName(object.to_product_unit_name != null ? object.to_product_unit_name : '');
        setRemark(object.remark != null ? object.remark : '');
        calculateToProductCost(object);
    }

    const onDataChange = (e: any) => {
        let key: string = String(e.target.name);
        let value: any = (
            e.target.type === 'checkbox'
                ? (e.target.checked ? 1 : 0)
                : e.target.type === 'radio' ? (e.target.value ? 1 : 0)
                : typeof(e.target.value) === 'number' ? Number(e.target.value)
                : String(e.target.value)
        );
        let object: any = data;
        if (key == 'from_quantity') {
            object['to_quantity'] = calculateToProduct(value);
        }
        object[key] = value;

        setData(object);
        setInputData(object);
        prop.onChange(object);
    }

    const calculateToProduct = (value: number) => {
        // destination_quantity = actual_source_quantity / config_from_quantity * config_to_quantity
        let qty = isEmpty(value) ? 0 : Number(value);
        let fromRatio = configFromQuantity > 0 ? configFromQuantity : 1;
        return (qty / fromRatio) * configToQuantity;
    }

    const calculateToProductCost = (object: any) => {
        // destination_unit_cost = source_total_cost / destination_quantity
        //   = (from_cost * actual_qty) / (actual_qty / config_from_quantity * config_to_quantity)
        //   = from_cost * config_from_quantity / config_to_quantity
        // -- independent of the actual quantity entered (a linear 1:N
        // conversion), but NOT independent of the formula's own ratio:
        // defaulting config_from_quantity to 1 only holds for a 1->N
        // formula; ignoring it (as before) silently mis-prices any M->N
        // formula where M != 1.
        let from_cost = isEmpty(object['from_product_cost']) ? 0 : parseFloat(object['from_product_cost'])
        let from_ratio = isEmpty(object['config_from_quantity']) ? 1 : parseFloat(object['config_from_quantity'])
        let to_qty = isEmpty(object['config_to_quantity']) ? 0 : parseFloat(object['config_to_quantity'])
        let total = (from_cost * from_ratio) / to_qty;
        setToProductCost(Number.isNaN(total) ? 0 : total);
    }

    const onSearchInputChange = async (e: any, sourceFlag: string) => {
        if (e.keyCode == 13) {
            let code = (e.target.value || '').trim();
            if (code) {
                try {
                    let targetProduct = await api_product_lookup_code(code);
                    if (targetProduct && (targetProduct.id || targetProduct.product_code)) {
                        setFlag(sourceFlag);
                        onManageProduct('A', targetProduct, sourceFlag);
                    } else {
                        let localP = productList.filter((x: any) => x['product_code'] == code);
                        if (localP.length > 0) {
                            setFlag(sourceFlag);
                            onManageProduct('A', localP[0], sourceFlag);
                        } else {
                            Modal.warning({
                                ...getConfigModal('แจ้งเตือน', 'ไม่พบสินค้าที่ค้นหา')
                            });

                            if (sourceFlag == 'F') {
                                setFromProductCode('');
                            } else {
                                setToProductCode('');
                            }
                        }
                    }
                } catch (err) {
                    let localP = productList.filter((x: any) => x['product_code'] == code);
                    if (localP.length > 0) {
                        setFlag(sourceFlag);
                        onManageProduct('A', localP[0], sourceFlag);
                    } else {
                        Modal.error({
                            ...getConfigModal('ข้อผิดพลาด', 'ค้นหาสินค้าไม่สำเร็จ กรุณาตรวจสอบการเชื่อมต่อ')
                        });

                        if (sourceFlag == 'F') {
                            setFromProductCode('');
                        } else {
                            setToProductCode('');
                        }
                    }
                }
            }
        }
    }

    const onManageProduct = async (key: string, e: any, sourceFlag?: string) => {
        let object: any = data;
        let sourceValue: string | undefined = flag;
        if (!isEmpty(sourceFlag)) {
            sourceValue = sourceFlag
        }

        if (key == 'A') {
            if (sourceValue == 'F') {
                object['from_product'] = e['id'];
                object['from_product_code'] = e['product_code'];
                object['from_product_name'] = e['product_name'];
                object['from_product_unit_name'] = e['product_unit_name'];
                object['from_product_cost'] = e['product_cost'];
                object['from_quantity'] = '1';

                let getSeparate = await api_config_separate('GET_BY_VALUE', {}, e['id']);
                if (isEmpty(getSeparate['to_product']) || getSeparate['to_product'] <= 0) {
                    object = initSeparate();
                    Modal.warning({
                        ...getConfigModal('แจ้งเตือน', 'สินค้านี้ไม่มีการตั้งค่าแยกแพค')
                    });
                } else {
                    object['to_product'] = getSeparate['to_product'];
                    object['to_product_code'] = getSeparate['to_product_code'];
                    object['to_product_name'] = getSeparate['to_product_name'];
                    object['to_product_unit_name'] = getSeparate['to_product_unit_name'];
                    // Read price_retail directly from the config_separate lookup
                    // response (already includes it) instead of cross-referencing
                    // the separately-loaded (possibly paginated / stale)
                    // productList — that lookup could miss the destination
                    // Product and throw here, aborting the rest of this block
                    // (config_to_quantity/to_quantity below) and leaving the
                    // Preview computing cost against a stale ratio.
                    object['to_product_price'] = getSeparate['price_retail'];
                    // getSeparate['from_quantity'] is the formula's own
                    // source-side ratio (config_separate.from_quantity) --
                    // distinct from object['from_quantity'] above, which is
                    // the user-editable actual quantity being separated.
                    object['config_from_quantity'] = getSeparate['from_quantity'];
                    object['config_to_quantity'] = getSeparate['config_to_quantity'];
                    object['to_quantity'] = getSeparate['to_quantity'];
                }
            }

            setData(object);
            setInputData(object);
            prop.onChange(object);
        }

        setFlag('');
    }

    const handleSelected = (e: any) => { 
        if (e.table == 'product') {
            onManageProduct('A', e);
        }

        setIsOpen(false);
    }

    const onOpenSelect = (table: string, flag: string) => {
        setFlag(flag);
        setTableSelect(table);
        setIsOpen(true);
    }

    const setFooter = () => {
        return <>
            <br />
            <Button className='btn-cancel' onClick={() => { setIsOpen(false); setTableSelect(''); }}>ยกเลิก</Button>
        </>
    }
   
    // Re-run whenever the parent hands us a DIFFERENT document to show, not
    // just on first mount. `SeparateDetail` sits inside a Drawer with
    // AntD's `destroyOnClose` -- that only guarantees a fresh mount if the
    // close animation actually finishes before the Drawer reopens. In the
    // real ADD -> Save -> immediately View-the-new-row flow, the next open
    // can fire before that animation settles, so the SAME instance survives
    // with the ADD form's leftover `data` state and `init()` (mount-only)
    // never re-fires for the new id -- confirmed by instrumenting init()
    // and reproducing the exact click sequence: with fast enough clicks,
    // only the id=0 (ADD) call ever logs, never a second call for the
    // newly-created document's id. Depending on the id here makes re-init
    // unconditional on whether a remount actually happened.
    useEffect(() => {
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prop.separateData.id]);
    
    return (
        <>
            <div className='detailContainer'>
                <div className='productFrom' style={{marginTop: '20px'}}>
                    <label className='title'>สินค้าต้นทาง</label>
                    <div className='headerBox'>
                        <div>
                            <div className='formInput'>
                                <label>ค้นหาสินค้า <span>*</span></label>
                                <Input name="from_product_code" value={fromProductCode} placeholder="รหัสสินค้า" onChange={onDataChange} onKeyUp={(e) => { onSearchInputChange(e, 'F') }} />
                                <Button className='btn-common' onClick={() => {onOpenSelect('product', 'F')}}><FontAwesomeIcon icon="magnifying-glass" /></Button>
                            </div>
                        </div>
                        <div>
                            <div className='formInput'>
                                <label>ชื่อสินค้า</label>
                                <Input name="from_product_name" value={fromProductName} placeholder="ชื่อสินค้า" disabled />
                            </div>
                        </div>
                    </div>
                    <div className='headerBox'>
                        <div>
                            <div className='formInput'>
                                <label>จำนวน <span>*</span></label>
                                <Input name="from_quantity" value={fromQuantity} placeholder="จำนวน" onChange={onDataChange} />
                                <span>{fromProductUnitName}</span>
                            </div>
                        </div>
                        <div>
                            <div className='formInput'>
                                <label>ต้นทุน</label>
                                <Input name="from_product_cost" value={fromProductCost} placeholder="ต้นทุน" disabled />
                            </div>
                        </div>
                    </div>
                </div>

                {/* <div id="mouse-scroll">
                    <div>
                        <span className="down-arrow-1"></span>
                        <span className="down-arrow-2"></span>
                        <span className="down-arrow-3"></span>
                    </div>
                </div> */}

                <div className='productTo' style={{marginTop: '10%'}}>
                    <label className='title'>สินค้าปลายทาง</label>
                    <div className='headerBox'>
                        <div>
                            <div className='formInput'>
                                <label>ค้นหาสินค้า</label>
                                <Input name="to_product_code" value={toProductCode} placeholder="รหัสสินค้า" disabled />
                            </div>
                        </div>
                        <div>
                            <div className='formInput'>
                                <label>ชื่อสินค้า</label>
                                <Input name="to_product_name" value={toProductName} placeholder="ชื่อสินค้า" disabled />
                            </div>
                        </div>
                    </div>
                    <div className='headerBox'>
                        <div>
                            <div className='formInput'>
                                <label>จำนวน</label>
                                <Input name="to_quantity" value={toQuantity} placeholder="จำนวน" disabled />
                                <span>{toProductUnitName}</span>
                            </div>
                        </div>
                        <div>
                            <div className='formInput'>
                                <label>ราคาต่อชิ้น</label>
                                <Input name="to_product_price" value={toProductPrice} placeholder="ราคาต่อชิ้น" onChange={onDataChange} />
                            </div>
                        </div>
                    </div>
                    <div className='headerBox'>
                        <div>
                            <div className='formInput'>
                                <label>ต้นทุนต่อชิ้น</label>
                                <Input name="to_product_cost" value={toProductCost} placeholder="ต้นทุนต่อชิ้น" onChange={onDataChange} disabled />
                            </div>
                        </div>
                    </div>
                </div>

                <div className='formInput' style={{justifyContent: "flex-end", marginTop: "20px"}}>
                    <label>หมายเหตุ</label>
                    <Input name="remark" value={remark} placeholder="หมายเหตุ" onChange={onDataChange} style={{marginLeft: "10px"}} />
                </div>

            </div>

            <Modal
                closeIcon={false}
                centered
                open={isOpen}
                footer={setFooter()}
                width={780}
                destroyOnClose={true}
            >
                <CommonSearch table={tableSelect} onSelect={handleSelected} />
            </Modal>
        </>
    );
}

export default SeparateDetail;
